import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';
import * as THREE from 'three';
import * as htmlToImage from 'html-to-image';
import { MODEL_UPLOAD_MAX_SIZE_MB } from '../constants/modelUpload';

/**
 * Validates file size against a maximum limit
 * @param file - File or Blob to validate
 * @param maxSizeMB - Maximum size in MB (default: 50MB)
 * @returns true if file size is within limit, false otherwise
 */
export function validateFileSize(
  file: File | Blob,
  maxSizeMB: number = MODEL_UPLOAD_MAX_SIZE_MB
): boolean {
  const maxBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxBytes;
}

let glbExporter: GLTFExporter = new GLTFExporter();

function isLodMaterial(m: THREE.Material): boolean {
  return (
    m instanceof THREE.ShaderMaterial &&
    (m as THREE.ShaderMaterial & { isLodMaterial?: boolean }).isLodMaterial === true
  );
}

/**
 * ThatOpen Fragments use LodMaterial (ShaderMaterial): IFC color lives in `lodColor`, not `Material.color`.
 * Reading the wrong field caused random / per-entity rainbow colors in GLB.
 */
function extractSurfaceAlbedo(m: THREE.Material): THREE.Color {
  if (isLodMaterial(m)) {
    const u = (m as THREE.ShaderMaterial).uniforms?.lodColor?.value;
    if (u instanceof THREE.Color) return u.clone();
  }
  if (m instanceof THREE.MeshStandardMaterial || m instanceof THREE.MeshPhysicalMaterial) {
    return m.color.clone();
  }
  const c = (m as THREE.Material & { color?: THREE.Color }).color;
  if (c instanceof THREE.Color) return c.clone();
  return new THREE.Color(0xaeaeae);
}

function extractSurfaceOpacity(m: THREE.Material): { opacity: number; transparent: boolean } {
  if (isLodMaterial(m)) {
    const op = (m as THREE.ShaderMaterial).uniforms?.lodOpacity?.value;
    const opacity = typeof op === 'number' ? op : 1;
    return { opacity, transparent: opacity < 1 || !!m.transparent };
  }
  return { opacity: m.opacity ?? 1, transparent: !!m.transparent };
}

/** Vertex/instance color buffers are often IDs or debug tints; GLTF then looks rainbow in model-viewer. */
function stripNonStandardColorAttributes(geometry: THREE.BufferGeometry) {
  for (const name of ['color', 'instanceColor'] as const) {
    if (geometry.hasAttribute(name)) geometry.deleteAttribute(name);
  }
}

/**
 * ThatOpen Fragments often use ShaderMaterial / custom materials GLTFExporter and model-viewer cannot use.
 * Clone the tree and swap to PBR / line basics so the GLB is valid for model-viewer.
 */
function cloneForGltfExport(root: THREE.Object3D): {
  root: THREE.Object3D;
  ownedGeometries: Set<THREE.BufferGeometry>;
  ownedMaterials: Set<THREE.Material>;
} {
  const copy = root.clone(true);
  const ownedGeometries = new Set<THREE.BufferGeometry>();
  const ownedMaterials = new Set<THREE.Material>();
  copy.traverse((obj) => {
    if (obj instanceof THREE.Mesh) {
      const geom = obj.geometry;
      if (
        geom instanceof THREE.BufferGeometry &&
        (geom.hasAttribute('color') || geom.hasAttribute('instanceColor'))
      ) {
        const exportGeometry = geom.clone();
        stripNonStandardColorAttributes(exportGeometry);
        obj.geometry = exportGeometry;
        ownedGeometries.add(exportGeometry);
      }
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      const next = mats.map((m) => {
        if (!m) {
          const material = new THREE.MeshStandardMaterial({ side: THREE.DoubleSide });
          ownedMaterials.add(material);
          return material;
        }
        if (m instanceof THREE.MeshStandardMaterial || m instanceof THREE.MeshPhysicalMaterial) {
          return m;
        }
        const color = extractSurfaceAlbedo(m);
        const { opacity, transparent } = extractSurfaceOpacity(m);
        const material = new THREE.MeshStandardMaterial({
          color,
          metalness: 0.2,
          roughness: 0.78,
          side: THREE.DoubleSide,
          transparent,
          opacity,
        });
        ownedMaterials.add(material);
        return material;
      });
      obj.material = next.length === 1 ? next[0] : next;
    } else if (obj instanceof THREE.LineSegments || obj instanceof THREE.Line) {
      const m = obj.material;
      if (m && !(m instanceof THREE.LineBasicMaterial)) {
        const material = new THREE.LineBasicMaterial({ color: 0x888888 });
        obj.material = material;
        ownedMaterials.add(material);
      }
    }
  });
  return { root: copy, ownedGeometries, ownedMaterials };
}

function disposeGltfExportClone(prepared: ReturnType<typeof cloneForGltfExport>) {
  for (const geometry of prepared.ownedGeometries) geometry.dispose();
  for (const material of prepared.ownedMaterials) material.dispose();
  prepared.ownedGeometries.clear();
  prepared.ownedMaterials.clear();
}

export function exportGlb(mesh: THREE.Object3D, modelName: string): Promise<Blob | null> {
  const prepared = cloneForGltfExport(mesh);
  const options = {
    binary: true,
    // Fragments may keep tiles invisible until a camera pass; exporting only visible can yield empty GLB.
    onlyVisible: false,
    includeCustomExtensions: false,
  };

  return new Promise<Blob | null>((resolve, reject) => {
    glbExporter.parse(
      prepared.root,
      (result) => {
        disposeGltfExportClone(prepared);
        const blob =
          result instanceof ArrayBuffer
            ? new Blob([result], { type: 'model/gltf-binary' })
            : new Blob([JSON.stringify(result, null, 2)], { type: 'model/gltf+json' });
        resolve(blob);
      },
      (error) => {
        disposeGltfExportClone(prepared);
        console.error(error);
        reject(new Error('Error exporting GLB file.'));
      },
      options
    );
  });
}

export function downloadGlb(mesh: THREE.Object3D, modelName: string): void {
  exportGlb(mesh, modelName)
    .then((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        downloadFile(url, modelName.replace(/\.[^/.]+$/, '') + '.glb');
        URL.revokeObjectURL(url);
      } else {
        console.error('Error downloading GLB file.');
      }
    })
    .catch((error) => console.error(error));
}

/*
export function downloadGlb(mesh: THREE.Object3D, modelName: string): void {
    const options = {
        // trs - bool. Export position, rotation and scale instead of matrix per node. Default is false
        // maxTextureSize - int. Restricts the image maximum size (both width and height) to the given value. Default is Infinity.
        // animations - Array<AnimationClip>. List of animations to be included in the export.
        binary: true,
        onlyVisible: false,
        includeCustomExtensions: true,
    };
    glbExporter.parse(
        mesh,
        (result) => {
            console.log('blob', result instanceof ArrayBuffer);
            const blob =
                result instanceof ArrayBuffer
                    ? new Blob([result], { type: 'model/gltf-binary' }) // application/octet-stream'
                    : new Blob([JSON.stringify(result, null, 2)], {
                          type: 'model/gltf+json', //  'application/json'
                      });
            const url = URL.createObjectURL(blob);
            downloadFile(url, modelName.replace(/\.[^/.]+$/, '') + '.glb');
            URL.revokeObjectURL(url);
        },
        (error) => console.error(error),
        options,
    );
}
*/

export function downloadFile(url: string, filename: string) {
  const link = document.createElement('a');
  link.style.display = 'none';
  document.body.appendChild(link);
  link.href = url;
  link.download = filename;
  link.click();
  link.remove();
}

export type ExportModelAsGlbOptions = {
  modelFile: File | null;
  modelUrl: string;
  productId: string | undefined;
  nonGltfModel: THREE.Object3D | null;
  preparedGlb?: Blob | null;
  downloadGlb: (mesh: THREE.Object3D, name: string) => void;
  downloadFile: (url: string, filename: string) => void;
  /** Called when fetch of product model fails. */
  onError?: (opts: { title: string; message: string }) => void;
  /**
   * When exporting from an uploaded file, use this to get the .glb filename.
   * Default: file.name with extension replaced by .glb (regex).
   */
  getGlbFileName?: (file: File) => string;
};

const defaultGetGlbFileName = (file: File): string => file.name.replace(/\.\w+$/, '.glb');

/**
 * Exports the current model as GLB: either from uploaded file (and optional converted non-GLTF)
 * or by fetching the product GLB by id. Shared by CreateProduct and EditProduct.
 */
export function exportModelAsGlb(options: ExportModelAsGlbOptions): void {
  const {
    modelFile,
    modelUrl,
    productId,
    nonGltfModel,
    preparedGlb,
    downloadGlb,
    downloadFile: downloadFileFn,
    onError,
    getGlbFileName = defaultGetGlbFileName,
  } = options;

  if (modelFile) {
    if (preparedGlb) {
      const url = URL.createObjectURL(preparedGlb);
      downloadFileFn(url, getGlbFileName(modelFile));
      setTimeout(() => URL.revokeObjectURL(url), 100);
      return;
    }
    if (nonGltfModel) {
      const newName = getGlbFileName(modelFile);
      downloadGlb(nonGltfModel, newName);
    } else {
      downloadFileFn(modelUrl, modelFile.name);
    }
    return;
  }

  if (productId && modelUrl) {
    fetch(modelUrl, { credentials: 'include' })
      .then((res) => res.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(new Blob([blob], { type: 'model/gltf-binary' }));
        downloadFileFn(blobUrl, `product_${productId}.glb`);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      })
      .catch((err) => {
        console.error('Error downloading model:', err);
        onError?.({ title: 'Fehler', message: 'Das Modell konnte nicht heruntergeladen werden.' });
      });
  }
}

export function extractPureName(fileName: string): string {
  // Remove the file extension
  const nameWithoutExtension = fileName.replace(/\.[^.]+$/, '');

  // Remove the UUID
  const pureName = nameWithoutExtension.replace(
    /_[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i,
    ''
  );

  return pureName;
}

export function dataURLtoFile(dataUrl: string, filename: string): File {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}

/*
export function copyToClipboard(link: string) {
    const textField = document.createElement('textarea');
    textField.innerText = link;
    document.body.appendChild(textField);
    textField.select();
    document.execCommand('copy');
    textField.remove();
}
*/

export function copyToClipboard(link: string) {
  navigator.clipboard
    .writeText(link)
    .then(() => {
      console.log('Link copied to clipboard:', link);
      // You can show a success message or perform any additional actions here
    })
    .catch((error) => {
      console.error('Error copying link to clipboard:', error);
      // You can show an error message or handle the error in any desired way
    });
}

export async function shareDivScreenshot(div: HTMLElement) {
  try {
    const dataUrl = await htmlToImage.toPng(div);

    // Create a new shareable file using the image data
    const file = dataURLtoFile(dataUrl, 'combined_image.png');

    // Perform the sharing action
    if (navigator.share) {
      await navigator.share({
        files: [file],
      });
    } else {
      // Fallback for browsers that don't support the Web Share API
      console.log('Sharing not supported in this browser');
      // You can implement your own custom sharing options here
      // For example, you can open a new window with a shareable link or display a sharing dialog
    }
  } catch (error) {
    console.error('Error during sharing:', error);
  }
}

/*
function getBase64FromURI(uri: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        fetch(uri)
            .then((response) => response.blob())
            .then((blob) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const base64String = reader.result as string;
                    resolve(base64String);
                };
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            })
            .catch((error) => {
                reject(error);
            });
    });
}
const base64String = await getBase64FromURI(previewImg);
formData.append('previewImage', base64String);
*/
